import Sdk from 'api.digitalpages.module.sdk.api';

export class StoreProductsService {
  /**
   * Busca produtos disponíveis para um connector específico
   * @param {string} connectorUid - UID do connector
   * @returns {Promise<Array>} Lista de produtos
   */
  static async getProductsByConnector(connectorUid) {
    try {
      if (!Sdk.authorization.authenticated) {
        throw new Error('Usuário não autenticado');
      }

      if (!connectorUid) {
        throw new Error('ConnectorUid é obrigatório');
      }

      const response = await Sdk.dynamic.bridge(
        `store/v1.0/product/connector/uid/${connectorUid}/management/available`
      );

      return response || [];
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      throw error;
    }
  }

  /**
   * Busca um produto específico por UID
   * @param {string} productUid - UID do produto
   * @returns {Promise<Object>} Dados do produto
   */
  static async getProductByUid(productUid) {
    try {
      if (!Sdk.authorization.authenticated) {
        throw new Error('Usuário não autenticado');
      }

      if (!productUid) {
        throw new Error('ProductUid é obrigatório');
      }

      const response = await Sdk.dynamic.bridge(
        `store/v1.0/product/uid/${productUid}/management`
      );

      return response;
    } catch (error) {
      console.error('Erro ao buscar produto:', error);
      throw error;
    }
  }

  /**
   * Exclui um produto
   * @param {string} productUid - UID do produto
   * @returns {Promise<void>}
   */
  static async deleteProduct(productUid) {
    try {
      if (!Sdk.authorization.authenticated) {
        throw new Error('Usuário não autenticado');
      }

      if (!productUid) {
        throw new Error('ProductUid é obrigatório');
      }

      await Sdk.dynamic.bridge(
        `store/v1.0/product/uid/${productUid}/management`,
        null,
        'DELETE'
      );
    } catch (error) {
      console.error('Erro ao excluir produto:', error);
      throw error;
    }
  }

  /**
   * Cria um novo produto
   * @param {string} connectorUid - UID do connector
   * @param {Object} productData - Dados do produto
   * @returns {Promise<Object>} Produto criado
   */
  static async createProduct(connectorUid, productData) {
    try {
      if (!Sdk.authorization.authenticated) {
        throw new Error('Usuário não autenticado');
      }

      if (!connectorUid) {
        throw new Error('ConnectorUid é obrigatório');
      }

      // Validar dados obrigatórios
      const requiredFields = ['name', 'currency', 'amount', 'unique_name'];
      for (const field of requiredFields) {
        if (!productData[field]) {
          throw new Error(`Campo obrigatório: ${field}`);
        }
      }

      // Preparar dados para envio
      const payload = {
        name: productData.name.trim(),
        currency: productData.currency,
        amount: parseFloat(productData.amount),
        unique_name: productData.unique_name.trim(),
        installments_max: parseInt(productData.installments_max) || 0,
        installments_tax: (productData.installments_tax || []).map(tax => parseFloat(tax) || 0),
        type: productData.type || 'NotConsumable',
      };

      const response = await Sdk.dynamic.bridge(
        `store/v1.0/product/connector/uid/${connectorUid}`,
        payload,
        'POST'
      );

      return response;
    } catch (error) {
      console.error('Erro ao criar produto:', error);
      throw error;
    }
  }

  /**
   * Atualiza um produto existente
   * @param {string} productUid - UID do produto
   * @param {Object} productData - Dados atualizados do produto
   * @returns {Promise<Object>} Produto atualizado
   */
  static async updateProduct(productUid, productData) {
    try {
      if (!Sdk.authorization.authenticated) {
        throw new Error('Usuário não autenticado');
      }

      if (!productUid) {
        throw new Error('ProductUid é obrigatório');
      }

      const response = await Sdk.dynamic.bridge(
        `store/v1.0/product/uid/${productUid}/management`,
        productData,
        'PUT'
      );

      return response;
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      throw error;
    }
  }

  /**
   * Busca informações do connector
   * @param {string} connectorUid - UID do connector
   * @returns {Object|null} Informações do connector
   */
  static getConnectorInfo(connectorUid) {
    try {
      if (!Sdk.authorization.authenticated) {
        return null;
      }

      const activeProject = Sdk.Api.authorization.activeProject;
      if (activeProject && activeProject.connectors) {
        return activeProject.connectors.find(c => c.uid === connectorUid) || null;
      }

      return null;
    } catch (error) {
      console.error('Erro ao buscar informações do connector:', error);
      return null;
    }
  }

  /**
   * Formata o valor monetário para exibição
   * @param {number} amount - Valor numérico
   * @param {string} currency - Código da moeda
   * @returns {string} Valor formatado
   */
  static formatCurrency(amount, currency = 'BRL') {
    try {
      const numericAmount = Number(amount);
      if (isNaN(numericAmount)) return 'R$ 0,00';

      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: currency === 'BRL' ? 'BRL' : 'USD',
      }).format(numericAmount);
    } catch (error) {
      return `${currency} ${Number(amount).toFixed(2)}`;
    }
  }

  /**
   * Formata a data para exibição
   * @param {string} dateString - String da data
   * @returns {string} Data formatada
   */
  static formatDate(dateString) {
    try {
      if (!dateString) return '-';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }
}
